/**
 * Frontend Logger Utility
 * Provides structured logging with environment-aware behavior
 * Sends errors to backend for centralized logging in production
 *
 * Usage:
 *   import logger from '../utils/logger';
 *   logger.debug('Debug message', { data });
 *   logger.info('Info message', { data });
 *   logger.warn('Warning message', { data });
 *   logger.error('Error message', { error, context });
 */

const isDevelopment = process.env.NODE_ENV === 'development';

// Get API base URL for error logging
const getApiBase = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'yobitch.fun' || hostname === 'www.yobitch.fun') {
      return process.env.REACT_APP_RAILWAY_URL || 'https://api.yobitch.fun';
    }
    if (hostname.includes('vercel.app')) {
      return process.env.REACT_APP_RAILWAY_URL || 'https://api.yobitch.fun';
    }
  }
  return 'http://localhost:3001';
};

// Buffer for batch error logging
let errorBuffer = [];
let flushTimeout = null;
const FLUSH_INTERVAL = 5000; // 5 seconds
const MAX_BUFFER_SIZE = 10;

/**
 * Send error to backend (non-blocking)
 */
const sendToBackend = async (level, message, data) => {
  // Only send errors and warnings in production
  if (isDevelopment && level !== 'error') return;
  
  const errorPayload = {
    level,
    message: String(message).slice(0, 1000),
    stack: data?.stack || (data instanceof Error ? data.stack : null),
    context: data && !(data instanceof Error) ? data : null,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    url: typeof window !== 'undefined' ? window.location.href : null,
    timestamp: new Date().toISOString()
  };
  
  errorBuffer.push(errorPayload);
  
  // Flush immediately if buffer is full or if it's an error
  if (errorBuffer.length >= MAX_BUFFER_SIZE || level === 'error') {
    flushErrors();
  } else if (!flushTimeout) {
    // Schedule flush
    flushTimeout = setTimeout(flushErrors, FLUSH_INTERVAL);
  }
};

/**
 * Flush buffered errors to backend
 */
const flushErrors = async () => {
  if (flushTimeout) {
    clearTimeout(flushTimeout);
    flushTimeout = null;
  }
  
  if (errorBuffer.length === 0) return;
  
  const toSend = [...errorBuffer];
  errorBuffer = [];
  
  try {
    const apiBase = getApiBase();
    
    if (toSend.length === 1) {
      // Single error
      await fetch(`${apiBase}/api/logging/error`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toSend[0])
      });
    } else {
      // Batch
      await fetch(`${apiBase}/api/logging/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ errors: toSend })
      });
    }
  } catch {
    // Silently fail - don't create infinite loop of errors
  }
};

/**
 * Format log data for consistent output
 * @param {*} data - Data to format
 * @returns {string} Formatted data string
 */
const formatData = (data) => {
  if (data === undefined || data === null) return '';
  if (typeof data === 'object') {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }
  return String(data);
};

/**
 * Get timestamp for log entries
 * @returns {string} ISO timestamp
 */
const getTimestamp = () => new Date().toISOString();

const logger = {
  /**
   * Debug level logging - only in development
   * @param {string} message - Log message
   * @param {*} [data] - Optional data to log
   */
  debug: (message, data) => {
    if (isDevelopment) {
      console.log(`[${getTimestamp()}] [DEBUG] ${message}`, data !== undefined ? data : '');
    }
  },

  /**
   * Info level logging
   * @param {string} message - Log message
   * @param {*} [data] - Optional data to log
   */
  info: (message, data) => {
    console.info(`[${getTimestamp()}] [INFO] ${message}`, data !== undefined ? data : '');
  },

  /**
   * Warning level logging
   * @param {string} message - Log message
   * @param {*} [data] - Optional data to log
   */
  warn: (message, data) => {
    console.warn(`[${getTimestamp()}] [WARN] ${message}`, data !== undefined ? data : '');
    
    // Send warnings to backend in production
    if (!isDevelopment) {
      sendToBackend('warn', message, data);
    }
  },

  /**
   * Error level logging with backend tracking
   * @param {string} message - Log message
   * @param {*} [data] - Optional error or context data
   */
  error: (message, data) => {
    const errorData = data !== undefined ? data : '';
    console.error(`[${getTimestamp()}] [ERROR] ${message}`, errorData);

    // Send to backend for centralized logging
    sendToBackend('error', message, data);
  },

  /**
   * Group related logs together
   * @param {string} label - Group label
   */
  group: (label) => {
    if (isDevelopment) {
      console.group(label);
    }
  },

  /**
   * End a log group
   */
  groupEnd: () => {
    if (isDevelopment) {
      console.groupEnd();
    }
  },

  /**
   * Log performance timing
   * @param {string} label - Timer label
   */
  time: (label) => {
    if (isDevelopment) {
      console.time(label);
    }
  },

  /**
   * End performance timing
   * @param {string} label - Timer label
   */
  timeEnd: (label) => {
    if (isDevelopment) {
      console.timeEnd(label);
    }
  }
};

export default logger;
